import React, { useState, useEffect } from 'react';
import { Editor, EditorProvider } from 'react-simple-wysiwyg';
import { emailTemplateAPI } from '../../services/api';
import { TableSkeleton } from '../../components/skeletons';

const GREEN = '#1B5C3A', CHAR = '#1a1a1a', MUTED = '#6b7280', BORDER = '#e5e7eb';

const EVENT_OPTIONS = {
  buyer: [
    { value: 'WELCOME_BUYER', label: 'Welcome & Onboarding' },
    { value: 'DEMAND_POSTED', label: 'Demand Confirmation' },
    { value: 'NEW_OFFER_RECEIVED', label: 'New Offer Alert' },
    { value: 'DEMAND_EXPIRY_WARNING', label: 'Demand Expiry Warning' },
    { value: 'DEAL_SECURED', label: 'Deal Secured / Accepted' },
    { value: 'REGISTRATION_OTP', label: 'Registration OTP' },
    { value: 'FORGOT_PASSWORD_OTP', label: 'Forgot Password OTP' },
  ],
  vendor: [
    { value: 'KYC_STATUS_ACCEPT', label: 'KYC Approved / Accepted' },
    { value: 'KYC_STATUS_REJECT', label: 'KYC Rejected' },
    { value: 'NEW_MATCHING_LEAD', label: 'New Matching Lead Alert' },
    { value: 'LOW_WALLET_BALANCE', label: 'Low Wallet Balance' },
    { value: 'WALLET_RECHARGE_SUCCESS', label: 'Wallet Recharge Success' },
    { value: 'MONTHLY_GST_INVOICE', label: 'Monthly GST Invoice' },
    { value: 'BID_ACCEPTED_BY_BUYER', label: 'Bid Accepted by Buyer' },
    { value: 'WELCOME_VENDOR', label: 'Welcome & Onboarding' },
    { value: 'REGISTRATION_OTP', label: 'Registration OTP' },
    { value: 'FORGOT_PASSWORD_OTP', label: 'Forgot Password OTP' },
    { value: 'FREE_SIGNUP_CREDIT', label: 'Free Sign Up Credit Bonus' },
    { value: 'REQUEST_CREDIT_ADDED', label: 'Request Credit Amount Added' },
  ],
  admin: [
    { value: 'NEW_KYC_SUBMISSION', label: 'New KYC Submission' },
    { value: 'HIGH_VALUE_DEMAND', label: 'High-Value Demand Alert' },
    { value: 'FAILED_PAYMENT_ALERT', label: 'Failed Payment Alert' },
  ]
};

export default function EmailTemplates() {
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('All');
  const [showForm, setShowForm] = useState(false);
  const [showPreview, setShowPreview] = useState(null);
  const [editingId, setEditingId] = useState(null);
  const [saving, setSaving] = useState(false);
  
  const [form, setForm] = useState({
    name: '',
    category: 'buyer',
    trigger: '',
    subject: '',
    body: ''
  });

  const loadTemplates = () => {
    setLoading(true);
    emailTemplateAPI.list({ category: filter })
      .then(r => setTemplates(r.data.data || []))
      .catch(() => {})
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    loadTemplates();
  }, [filter]);

  const handleSave = async () => {
    if (!form.name || !form.trigger || !form.subject || !form.body) {
      return alert('All fields are required.');
    }
    setSaving(true);
    try {
      if (editingId) {
        await emailTemplateAPI.update(editingId, form);
      } else {
        await emailTemplateAPI.create(form);
      }
      setShowForm(false);
      resetForm();
      loadTemplates();
    } catch (err) {
      alert(err.response?.data?.message || 'Failed to save template.');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this template?')) return;
    try {
      await emailTemplateAPI.remove(id);
      loadTemplates();
    } catch (err) {
      alert('Failed to delete template.');
    }
  };

  const openEdit = (t) => {
    setEditingId(t._id);
    setForm({
      name: t.name,
      category: t.category,
      trigger: t.trigger,
      subject: t.subject,
      body: t.body
    });
    setShowForm(true);
  };

  const resetForm = () => {
    setEditingId(null);
    setForm({ name: '', category: 'buyer', trigger: '', subject: '', body: '' });
  };

  const filtered = templates.filter(t => filter === 'All' || t.category === filter.toLowerCase());

  if (loading && templates.length === 0) return <TableSkeleton rows={5} columns={4} style={{ padding: 24 }} />;

  return (
    <div style={{ padding: 24 }}>
      {/* Header */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 24 }}>
        <div>
          <h1 style={{ fontFamily: 'var(--font-serif)', fontSize: 26, fontWeight: 700, color: CHAR }}>Email Templates</h1>
          <p style={{ color: MUTED, fontSize: 13, marginTop: 4 }}>Manage dynamic email content for automated events</p>
        </div>
        <button 
          onClick={() => { resetForm(); setShowForm(true); }}
          style={{ background: GREEN, color: '#fff', border: 'none', borderRadius: 9, padding: '10px 20px', fontSize: 13, fontWeight: 700, cursor: 'pointer' }}
        >
          + Add Template
        </button>
      </div>

      {/* Filter Bar */}
      <div style={{ background: '#fff', border: `1px solid ${BORDER}`, borderRadius: 12, padding: '12px 16px', marginBottom: 20, display: 'flex', alignItems: 'center', gap: 12 }}>
        <span style={{ fontSize: 12, fontWeight: 700, color: MUTED, textTransform: 'uppercase' }}>Filter Category:</span>
        <select 
          value={filter} 
          onChange={e => setFilter(e.target.value)}
          style={{ padding: '6px 12px', borderRadius: 6, border: `1px solid ${BORDER}`, fontSize: 13, color: CHAR, outline: 'none' }}
        >
          <option value="All">All Categories</option>
          <option value="Buyer">Buyer</option>
          <option value="Vendor">Vendor</option>
        </select>
      </div>

      {/* Table */}
      <div style={{ background: '#fff', border: `1px solid ${BORDER}`, borderRadius: 14, overflow: 'hidden' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
          <thead>
            <tr style={{ background: '#f9fafb' }}>
              {['Template Name', 'Category', 'Event Trigger', 'Subject', 'Actions'].map(h => (
                <th key={h} style={{ padding: '14px', textAlign: 'left', fontSize: 11, fontWeight: 700, color: MUTED, textTransform: 'uppercase', borderBottom: `2px solid ${BORDER}` }}>{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {filtered.length === 0 ? (
              <tr><td colSpan={5} style={{ padding: 40, textAlign: 'center', color: MUTED }}>No templates found</td></tr>
            ) : filtered.map(t => (
              <tr key={t._id} style={{ borderBottom: `1px solid ${BORDER}` }}>
                <td style={{ padding: '14px', fontSize: 14, fontWeight: 600, color: CHAR }}>{t.name}</td>
                <td style={{ padding: '14px' }}>
                  <span style={{ 
                    padding: '3px 8px', borderRadius: 12, fontSize: 10, fontWeight: 700, 
                    background: t.category === 'buyer' ? '#dcfce7' : '#e0e7ff', 
                    color: t.category === 'buyer' ? GREEN : '#3730a3',
                    textTransform: 'uppercase'
                  }}>
                    {t.category}
                  </span>
                </td>
                <td style={{ padding: '14px', fontSize: 12, fontFamily: 'monospace', color: MUTED }}>{t.trigger}</td>
                <td style={{ padding: '14px', fontSize: 13, color: CHAR }}>{t.subject}</td>
                <td style={{ padding: '14px' }}>
                  <div style={{ display: 'flex', gap: 8 }}>
                    <button onClick={() => setShowPreview(t)} title="View Preview" style={{ background: '#f3f4f6', border: 'none', borderRadius: 6, padding: '6px', cursor: 'pointer' }}>👁️</button>
                    <button onClick={() => openEdit(t)} title="Edit" style={{ background: '#f3f4f6', border: 'none', borderRadius: 6, padding: '6px', cursor: 'pointer' }}>✏️</button>
                    <button onClick={() => handleDelete(t._id)} title="Delete" style={{ background: '#fff', border: `1px solid ${BORDER}`, borderRadius: 6, padding: '6px', cursor: 'pointer', color: '#dc2626' }}>🗑️</button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Add/Edit Modal */}
      {showForm && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }} onClick={() => setShowForm(false)}>
          <div style={{ background: '#fff', borderRadius: 16, padding: 32, width: 700, maxHeight: '90vh', overflowY: 'auto', boxShadow: '0 20px 25px -5px rgba(0,0,0,0.1)' }} onClick={e => e.stopPropagation()}>
            <h2 style={{ fontFamily: 'var(--font-serif)', fontSize: 22, fontWeight: 700, marginBottom: 24, color: CHAR }}>
              {editingId ? 'Edit Email Template' : 'Add New Email Template'}
            </h2>
            
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16, marginBottom: 16 }}>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                <label style={{ fontSize: 12, fontWeight: 700, color: MUTED }}>Template Name *</label>
                <input 
                  style={{ padding: '10px 12px', borderRadius: 8, border: `1px solid ${BORDER}`, fontSize: 14, outline: 'none' }}
                  placeholder="e.g. Buyer Welcome Email"
                  value={form.name} onChange={e => setForm({ ...form, name: e.target.value })}
                />
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                <label style={{ fontSize: 12, fontWeight: 700, color: MUTED }}>Category *</label>
                <select 
                  style={{ padding: '10px 12px', borderRadius: 8, border: `1px solid ${BORDER}`, fontSize: 14, outline: 'none' }}
                  value={form.category} onChange={e => setForm({ ...form, category: e.target.value, trigger: '' })}
                >
                  <option value="buyer">Buyer</option>
                  <option value="vendor">Vendor</option>
                  <option value="admin">Admin</option>
                </select>
              </div>
            </div>

            <div style={{ display: 'flex', flexDirection: 'column', gap: 6, marginBottom: 16 }}>
              <label style={{ fontSize: 12, fontWeight: 700, color: MUTED }}>Event Trigger *</label>
              <select 
                style={{ padding: '10px 12px', borderRadius: 8, border: `1px solid ${BORDER}`, fontSize: 14, outline: 'none', background: '#fff' }}
                value={form.trigger} 
                onChange={e => setForm({ ...form, trigger: e.target.value })}
              >
                <option value="">Select an Event</option>
                {(EVENT_OPTIONS[form.category] || []).map(opt => (
                  <option key={opt.value} value={opt.value}>{opt.label} ({opt.value})</option>
                ))}
              </select>
            </div>

            <div style={{ display: 'flex', flexDirection: 'column', gap: 6, marginBottom: 20 }}>
              <label style={{ fontSize: 12, fontWeight: 700, color: MUTED }}>Email Subject Line *</label>
              <input 
                style={{ padding: '10px 12px', borderRadius: 8, border: `1px solid ${BORDER}`, fontSize: 14, outline: 'none' }}
                placeholder="Subject shown in inbox"
                value={form.subject} onChange={e => setForm({ ...form, subject: e.target.value })}
              />
            </div>

            <div style={{ display: 'flex', flexDirection: 'column', gap: 6, marginBottom: 24 }}>
              <label style={{ fontSize: 12, fontWeight: 700, color: MUTED }}>Email Body (Rich Text) *</label>
              <div style={{ minHeight: 250, marginBottom: 10 }}>
                <EditorProvider>
                  <Editor 
                    value={form.body} 
                    onChange={e => setForm({ ...form, body: e.target.value })} 
                    style={{ minHeight: 200, border: `1px solid ${BORDER}`, borderRadius: 8 }}
                  />
                </EditorProvider>
              </div>
            </div>

            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 12, borderTop: `1px solid ${BORDER}`, paddingTop: 20 }}>
              <button 
                onClick={() => setShowForm(false)}
                style={{ padding: '10px 20px', borderRadius: 9, border: `1px solid ${BORDER}`, background: '#fff', fontSize: 13, fontWeight: 600, color: MUTED, cursor: 'pointer' }}
              >
                Cancel
              </button>
              <button 
                onClick={handleSave}
                disabled={saving}
                style={{ padding: '10px 24px', borderRadius: 9, border: 'none', background: GREEN, color: '#fff', fontSize: 13, fontWeight: 700, cursor: 'pointer' }}
              >
                {saving ? 'Saving...' : 'Save Template'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Preview Modal */}
      {showPreview && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }} onClick={() => setShowPreview(null)}>
          <div style={{ background: '#fff', borderRadius: 16, width: 600, maxHeight: '80vh', overflowY: 'auto', boxShadow: '0 20px 25px -5px rgba(0,0,0,0.1)' }} onClick={e => e.stopPropagation()}>
            <div style={{ padding: '16px 24px', borderBottom: `1px solid ${BORDER}`, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <h3 style={{ margin: 0, fontSize: 16, fontWeight: 700, color: CHAR }}>Email Preview</h3>
              <button onClick={() => setShowPreview(null)} style={{ background: 'none', border: 'none', fontSize: 20, cursor: 'pointer', color: MUTED }}>×</button>
            </div>
            <div style={{ padding: 24 }}>
              <div style={{ padding: '12px 16px', background: '#f9fafb', borderRadius: 8, marginBottom: 20, border: `1px solid ${BORDER}` }}>
                <div style={{ fontSize: 11, fontWeight: 700, color: MUTED, textTransform: 'uppercase', marginBottom: 4 }}>Subject</div>
                <div style={{ fontSize: 14, fontWeight: 600, color: CHAR }}>{showPreview.subject}</div>
              </div>
              <div 
                style={{ 
                  padding: 24, border: `1px solid ${BORDER}`, borderRadius: 8, minHeight: 300, 
                  fontFamily: 'sans-serif', fontSize: 14, lineHeight: 1.6, color: CHAR 
                }}
                dangerouslySetInnerHTML={{ __html: showPreview.body }}
              />
            </div>
            <div style={{ padding: '16px 24px', borderTop: `1px solid ${BORDER}`, textAlign: 'right' }}>
              <button 
                onClick={() => setShowPreview(null)}
                style={{ padding: '8px 20px', borderRadius: 8, border: `1px solid ${BORDER}`, background: '#fff', fontSize: 13, fontWeight: 600, color: CHAR, cursor: 'pointer' }}
              >
                Close Preview
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
